import { isClubStandLocation } from './clubWaypointBoard';
import { buildFeedingPayload } from './feedingPayload';

export const POINT_MARKER_TYPES = [
  { value: '', label: 'None' },
  { value: 'mineral_block', label: 'Mineral Block' },
  { value: 'camera', label: 'Camera' },
  { value: 'deer_sign', label: 'Deer Sign' },
  { value: 'gate', label: 'Gate' }
];

export const WIND_CODES = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

const SAVED_MARKER_TYPES = new Set(
  POINT_MARKER_TYPES.map((option) => option.value).filter(Boolean)
);

export function markerTypeSelectValue(markerType) {
  return SAVED_MARKER_TYPES.has(markerType) ? markerType : '';
}

export function markerTypeForSave({ isStand, markerType }) {
  if (isStand) return '';
  return markerTypeSelectValue(markerType);
}

export function isClubHuntLocation(feature) {
  return feature?.featureType === 'hunt_location' || feature?.isHuntLocation === true;
}

export function isStandFeature(feature) {
  if (isClubHuntLocation(feature)) {
    return isClubStandLocation(feature);
  }
  if (feature?.is_stand != null) return Boolean(feature.is_stand);
  return Boolean(
    (Array.isArray(feature?.optimal_winds) && feature.optimal_winds.length > 0) ||
    (feature?.optimal_wind && feature.optimal_wind !== '')
  );
}

export function isFeedFeature(feature) {
  return Boolean(
    feature?.feeding_mode ||
    feature?.consumption_rate_code ||
    feature?.percent_remaining != null ||
    feature?.marker_type === 'feeder'
  );
}

export function shouldEditAsWaypoint(feature) {
  const type = String(feature?.geojson?.type || feature?.type || '').toLowerCase();
  return type === 'point';
}

/** Club hunt-location points use the same Stand + Feed sheet as Drop waypoint. */
export function clubPointUsesWaypointEditor(feature) {
  return isClubHuntLocation(feature) && shouldEditAsWaypoint(feature);
}

export function waypointFormFromFeature(feature) {
  const winds = Array.isArray(feature?.optimal_winds) ? feature.optimal_winds : [];
  return {
    id: feature?.id ?? null,
    geometry: feature?.geojson || feature?.geometry || null,
    name: feature?.name || '',
    notes: feature?.notes || '',
    isStand: isStandFeature(feature),
    feed: isFeedFeature(feature),
    markerType: markerTypeSelectValue(feature?.marker_type),
    winds,
    feedingMode: feature?.feeding_mode || '',
    feedCapacityLbs: feature?.feed_capacity_lbs || null,
    consumptionRateCode: feature?.consumption_rate_code || '',
    refillThresholdPct: typeof feature?.refill_threshold_pct === 'number'
      ? feature.refill_threshold_pct
      : 20
  };
}

export const IDENTITY_TOGGLE_COPY = {
  ground: {
    stand: 'Hang a stand. Off is a marker.',
    feed: 'Counts toward feed management. Does not require a stand.'
  },
  club: {
    stand: 'Members can sign out and log a hunt here.',
    feed: 'Tracked in Feed & Seed. Any member can log a fill.'
  }
};

export function clubDrawnOpensWaypointModal(feature) {
  const type = String(feature?.geometry?.type || feature?.type || '').toLowerCase();
  return type === 'point';
}

export function buildClubHuntLocationPayload({
  tractId,
  geometry,
  name,
  notes = '',
  isStand,
  feed,
  winds,
  feedingMode,
  feedCapacityLbs,
  consumptionRateCode,
  refillThresholdPct
}) {
  const stand = Boolean(isStand);
  const identity = buildPointIdentityPayload({
    isStand: stand,
    feed,
    markerType: '',
    winds,
    feedingMode,
    feedCapacityLbs,
    consumptionRateCode,
    refillThresholdPct
  });
  const payload = {
    hunt_boundary: tractId,
    name: String(name || '').trim() || (stand ? 'Stand' : 'Marker'),
    geojson: geometry,
    notes: notes || '',
    feature_type: stand ? 'stand' : 'other',
    optimal_winds: identity.optimal_winds,
    feeding_mode: identity.feeding_mode,
    consumption_rate_code: identity.consumption_rate_code,
    feed_capacity_lbs: identity.feed_capacity_lbs
  };
  if (identity.refill_threshold_pct != null) {
    payload.refill_threshold_pct = identity.refill_threshold_pct;
  }
  return payload;
}

export function buildPointIdentityPayload({
  isStand,
  feed,
  markerType,
  winds,
  feedingMode,
  feedCapacityLbs,
  consumptionRateCode,
  refillThresholdPct
}) {
  const stand = Boolean(isStand);
  return {
    is_stand: stand,
    marker_type: markerTypeForSave({ isStand: stand, markerType }),
    optimal_winds: stand && Array.isArray(winds) ? winds : [],
    ...buildFeedingPayload({
      feed,
      feedingMode,
      feedCapacityLbs,
      consumptionRateCode,
      refillThresholdPct
    })
  };
}
